import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotepadDrawer } from './useNotepadDrawer';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { TasksTab } from './TasksTab';
import { NotesTab } from './NotesTab';
import { NotepadEditorPanel, NotepadEditorPanelRef } from './NotepadEditorPanel';
import { useNotepadEditorState, NotepadEditorContext } from './useNotepadEditorState';
import { NotepadConfirmDialog } from './NotepadConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { logger } from '@/lib/logger';

type NotepadTab = 'tasks' | 'notes';

/**
 * Notepad drawer - right side sheet with Tasks and Notes tabs
 * Keyboard: Cmd/Ctrl+J to toggle, ESC to close
 * Remembers last active tab in Firestore or localStorage
 */
export function NotepadDrawer() {
  const { t } = useTranslation(['notepad', 'common']);
  const { isOpen, close, pendingEdit, clearPendingEdit } = useNotepadDrawer();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<NotepadTab>('tasks');
  const [isLoading, setIsLoading] = useState(true);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [pendingTabSwitch, setPendingTabSwitch] = useState<NotepadTab | null>(null);

  // Editor state for in-drawer editing
  const editorState = useNotepadEditorState();

  // Handle pending edit from deep-link (e.g., /tasks?id=xxx)
  useEffect(() => {
    if (isOpen && pendingEdit && !editorState.entityId) {
      // Switch to the correct tab and open the editor
      if (pendingEdit.type === 'task') {
        setActiveTab('tasks');
        editorState.openEdit('task', pendingEdit.id);
      } else if (pendingEdit.type === 'note') {
        setActiveTab('notes');
        editorState.openEdit('note', pendingEdit.id);
      }
      clearPendingEdit();
    }
  }, [isOpen, pendingEdit, editorState, clearPendingEdit]);

  // Ref to editor panel for triggering save
  const editorPanelRef = useRef<NotepadEditorPanelRef>(null);

  // Load last active tab from Firestore or localStorage
  useEffect(() => {
    async function loadLastTab() {
      if (!user?.uid) {
        setIsLoading(false);
        return;
      }

      try {
        // Try Firestore first
        const prefsRef = doc(db, 'users', user.uid, 'private', 'notepadPrefs');
        const prefsSnap = await getDoc(prefsRef);

        if (prefsSnap.exists()) {
          const lastTab = prefsSnap.data()?.lastTab as NotepadTab;
          if (lastTab === 'tasks' || lastTab === 'notes') {
            setActiveTab(lastTab);
          }
        } else {
          // Fallback to localStorage
          const stored = localStorage.getItem('notepad:lastTab');
          if (stored === 'tasks' || stored === 'notes') {
            setActiveTab(stored);
          }
        }
      } catch (error) {
        logger.error('[NotepadDrawer] Failed to load last tab:', error);
        // Fallback to localStorage
        const stored = localStorage.getItem('notepad:lastTab');
        if (stored === 'tasks' || stored === 'notes') {
          setActiveTab(stored);
        }
      } finally {
        setIsLoading(false);
      }
    }

    void loadLastTab();
  }, [user?.uid]);

  // Save active tab to Firestore and localStorage
  const handleTabChange = useCallback(
    async (tab: string) => {
      const newTab = tab as NotepadTab;

      // If in editor mode and dirty, show confirm before switching
      if (editorState.mode !== 'list' && editorState.isDirty) {
        setPendingTabSwitch(newTab);
        setShowCloseConfirm(true);
        return;
      }

      // If in editor mode but not dirty, go back to list first
      if (editorState.mode !== 'list') {
        editorState.backToList();
      }

      setActiveTab(newTab);

      // Save to localStorage immediately
      localStorage.setItem('notepad:lastTab', newTab);

      // Save to Firestore in background
      if (user?.uid) {
        try {
          const prefsRef = doc(db, 'users', user.uid, 'private', 'notepadPrefs');
          await setDoc(prefsRef, { lastTab: newTab }, { merge: true });
        } catch (error) {
          logger.error('[NotepadDrawer] Failed to save last tab:', error);
        }
      }
    },
    [editorState, user?.uid]
  );

  // Handle drawer close with dirty check
  const handleDrawerClose = useCallback(() => {
    if (editorState.mode !== 'list' && editorState.isDirty) {
      setShowCloseConfirm(true);
      return;
    }
    close();
  }, [editorState.mode, editorState.isDirty, close]);

  // Handle confirm save from dialog
  const handleConfirmSave = useCallback(() => {
    editorPanelRef.current?.triggerSave();
    // The save callback will handle closing
  }, []);

  // Handle confirm discard from dialog
  const handleConfirmDiscard = useCallback(() => {
    setShowCloseConfirm(false);
    editorState.setDirty(false);

    if (pendingTabSwitch) {
      // Complete the tab switch
      editorState.backToList();
      setActiveTab(pendingTabSwitch);
      localStorage.setItem('notepad:lastTab', pendingTabSwitch);
      setPendingTabSwitch(null);
    } else {
      // Close the drawer
      close();
    }
  }, [editorState, pendingTabSwitch, close]);

  // Handle confirm cancel from dialog (stay editing)
  const handleConfirmCancel = useCallback(() => {
    setShowCloseConfirm(false);
    setPendingTabSwitch(null);
  }, []);

  // Reset editor state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      editorState.reset();
    }
  }, [isOpen, editorState]);

  if (isLoading) {
    return null; // or return a loading spinner
  }

  // Determine if we should show the editor panel
  const showEditor = editorState.mode !== 'list' && editorState.entityType !== null;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && handleDrawerClose()}>
        <SheetContent side="right" className="w-full max-w-md p-0 sm:max-w-xl">
          <NotepadEditorContext.Provider value={editorState}>
            {showEditor ? (
              // Editor Panel View
              <NotepadEditorPanel
                ref={editorPanelRef}
                mode={editorState.mode}
                entityType={editorState.entityType!}
                entityId={editorState.entityId}
                onBack={editorState.backToList}
                onSaved={editorState.backToList}
              />
            ) : (
              // List View with Tabs
              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className="h-full flex flex-col"
              >
                <SheetHeader className="px-6 py-6 border-b">
                  <SheetTitle className="text-2xl font-bold">
                    {t('notepad:drawer.title')}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    {t('notepad:drawer.description')}
                  </SheetDescription>
                  <TabsList className="w-full grid grid-cols-2 mt-4 h-11">
                    <TabsTrigger value="tasks" className="text-sm">
                      {t('notepad:drawer.tabs.tasks')}
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="text-sm">
                      {t('notepad:drawer.tabs.notes')}
                    </TabsTrigger>
                  </TabsList>
                </SheetHeader>

                <TabsContent value="tasks" className="flex-1 overflow-hidden">
                  <ErrorBoundary>
                    <TasksTab />
                  </ErrorBoundary>
                </TabsContent>

                <TabsContent value="notes" className="flex-1 overflow-hidden">
                  <div
                    id="notepad-drawer-portal"
                    className="relative h-full w-full overflow-hidden"
                  >
                    <ErrorBoundary>
                      <NotesTab />
                    </ErrorBoundary>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </NotepadEditorContext.Provider>
        </SheetContent>
      </Sheet>

      {/* Unsaved Changes Confirm Dialog (for drawer close / tab switch) */}
      <NotepadConfirmDialog
        open={showCloseConfirm}
        onOpenChange={setShowCloseConfirm}
        onSave={handleConfirmSave}
        onDiscard={handleConfirmDiscard}
        onCancel={handleConfirmCancel}
      />
    </>
  );
}
